import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { CommunityBanner } from "@/types/job.types";
import { BANNER_ASPECT_CLASSES, BANNER_SIZES, BANNER_COVER_HINT, type BannerType, type DisplayType } from "@/components/community/banner/bannerSizes";


interface BannerEditDialogProps {
  banner: CommunityBanner;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function BannerEditDialog({ banner, open, onOpenChange, onSuccess }: BannerEditDialogProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [type, setType] = useState<BannerType>(banner.type as BannerType);
  const [display, setDisplay] = useState<DisplayType>(banner.display as DisplayType);
  const [linkUrl, setLinkUrl] = useState(banner.link_url || "");
  const [imageUrl, setImageUrl] = useState(banner.image_url || "");

  const sizeInfo = BANNER_SIZES[type];
  const recommendedDimensions = sizeInfo[display];
  const previewAspectClass = BANNER_ASPECT_CLASSES[type][display];


  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({ title: "Formato não suportado", description: "Use JPG, PNG ou WebP", variant: "destructive" });
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast({ title: "Arquivo muito grande", description: "Tamanho máximo: 2MB", variant: "destructive" });
        return;
      }

      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(fileName, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('banners').getPublicUrl(fileName);
      setImageUrl(publicUrl);

      toast({ title: "Imagem enviada", description: "A nova imagem foi carregada com sucesso" });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ title: "Erro ao enviar imagem", description: "Tente novamente", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const { error } = await supabase
        .from("community_banners")
        .update({
          type,
          display,
          link_url: linkUrl || null,
          image_url: imageUrl,
        })
        .eq("id", banner.id);

      if (error) throw error;

      toast({ title: "Banner atualizado", description: "As alterações foram salvas com sucesso" });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating banner:', error);
      toast({ title: "Erro ao salvar", description: "Ocorreu um erro ao atualizar o banner", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw] h-[90vh] max-h-[90vh] overflow-y-auto' : 'sm:max-w-md'}`}>
        <DialogHeader>
          <DialogTitle className={isMobile ? 'text-lg' : ''}>Editar Banner</DialogTitle>
          <DialogDescription className={isMobile ? 'text-sm' : ''}>
            Altere o link, tipo, dispositivo ou imagem do banner
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image preview */}
          <div className="space-y-2">
            <Label>Imagem atual</Label>
            <div className={`relative w-full ${previewAspectClass} rounded-md overflow-hidden bg-muted`}>
              <img src={imageUrl} alt="Preview do banner" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Upload new image */}
          <div className={`relative flex flex-col items-center justify-center border-2 border-dashed border-primary/40 rounded-lg text-center ${isMobile ? 'p-4' : 'p-6'}`}>
            <Upload className={`text-primary mb-2 ${isMobile ? 'h-6 w-6' : 'h-8 w-8'}`} />
            <p className={`font-medium ${isMobile ? 'text-sm' : ''}`}>
              {isUploading ? "Enviando..." : "Trocar imagem"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              JPG, PNG, WebP • Máx 2MB • <strong>{recommendedDimensions}</strong> ({sizeInfo.note})
            </p>
            <p className="text-[11px] text-muted-foreground italic mt-1 px-2">
              {BANNER_COVER_HINT}
            </p>

            <input
              type="file"
              onChange={handleFileUpload}
              accept="image/jpeg,image/png,image/webp"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              disabled={isUploading}
            />
          </div>

          {/* Type select */}
          <div className="space-y-2">
            <Label>Tipo do Banner</Label>
            <Select value={type} onValueChange={(v: BannerType) => setType(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INTRODUCTION">Introdução</SelectItem>
                <SelectItem value="LEARNING">Conhecimento Técnico</SelectItem>
                <SelectItem value="QUESTIONS">Carreira & Vagas</SelectItem>
                <SelectItem value="HOME_ADS">Publicidade/Home</SelectItem>
                
                <SelectItem value="PUBLIC_JOBS">Vagas Públicas (/vagas)</SelectItem>
                <SelectItem value="FEED">Feed</SelectItem>
                <SelectItem value="GETTING_STARTED">Comece Aqui</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Display select */}
          <div className="space-y-2">
            <Label>Dispositivo</Label>
            <Select value={display} onValueChange={(v: DisplayType) => setDisplay(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DESKTOP">Desktop</SelectItem>
                <SelectItem value="MOBILE">Mobile</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Link URL */}
          <div className="space-y-2">
            <Label>Link de destino (opcional)</Label>
            <Input
              type="url"
              placeholder="https://exemplo.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
          </div>

          {/* Save button */}
          <Button onClick={handleSave} disabled={isSaving || isUploading} className="w-full">
            {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvando...</> : "Salvar Alterações"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
